// File: src/components/layout/Header.js
/**
 * Universal Header Component (Integrated with TopBar)
 * Combines company branding with navigation controls.
 *
 * Carries the global language switch (EN / ES / ع): visible in the desktop
 * nav and the mobile menu, it drives the same LanguageContext the section
 * content toggle uses, so switching here flips both the chrome and the
 * regulation text.
 */

import React, { useState } from 'react';
import { Menu, ArrowLeft, Home, Search, Globe, User, X, MoreVertical } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import { UI_LANGS } from '../../i18n/strings';
import { FEATURES } from '../../utils/constants';
import '../../styles/components/header.css';

const Header = ({
    onSearchClick,
    onLoginClick,
    loginLabel,
    title = "OSHA Regulations",
    showBackButton = false,
    onBackClick,
    showSidebarToggle = true,
    onSidebarToggle
}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { lang, setLang, t } = useLanguage();
    const languagesEnabled = FEATURES.CONTENT_TRANSLATIONS;

    const handleHomeClick = () => {
        window.location.href = '/';
    };

    const handleCDRiskClick = () => {
        window.open('https://cdrisk.com', '_blank', 'noopener,noreferrer');
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleSidebarToggle = () => {
        if (onSidebarToggle) {
            onSidebarToggle();
        }
    };

    // Compact segmented language switch (EN | ES | ع). `compact` uses the
    // short labels in the desktop header; the mobile menu shows full names.
    const renderLanguageSwitch = (compact) => (
        <div
            className={`header-lang-switch ${compact ? 'compact' : 'full'}`}
            role="group"
            aria-label={t('nav.language')}
        >
            {UI_LANGS.map(({ code, label, short }) => (
                <button
                    key={code}
                    type="button"
                    lang={code}
                    className={`header-lang-btn ${lang === code ? 'active' : ''}`}
                    onClick={() => {
                        setLang(code);
                        setMobileMenuOpen(false);
                    }}
                    aria-pressed={lang === code}
                    title={label}
                >
                    {compact ? short : label}
                </button>
            ))}
        </div>
    );

    return (
        <header className="app-header">
            <div className="header-container">
                {/* Left Section: Menu Toggle (if applicable) + Back Button + Brand */}
                <div className="header-left">
                    {/* Sidebar Toggle (for regulation navigation) */}
                    {showSidebarToggle && !showBackButton && (
                        <button
                            className="sidebar-toggle-btn"
                            onClick={handleSidebarToggle}
                            aria-label={t('nav.toggleSidebar')}
                        >
                            <Menu size={20} />
                        </button>
                    )}

                    {/* Back Button */}
                    {showBackButton && (
                        <button
                            className="back-button"
                            onClick={onBackClick}
                            aria-label={t('nav.goBack')}
                        >
                            <ArrowLeft size={20} />
                        </button>
                    )}

                    {/* Company Brand */}
                    <button
                        className="brand-link"
                        onClick={handleCDRiskClick}
                        aria-label="Critical Dynamics Risk Management"
                    >
                        <span className="brand-name">Critical Dynamics</span>
                        <span className="brand-subtitle">Risk Management</span>
                    </button>
                </div>

                {/* Center Section: Page Title (mobile) */}
                <div className="header-center mobile-only">
                    <h1 className="page-title">{title}</h1>
                </div>

                {/* Right Section: Navigation Buttons */}
                <div className="header-right">
                    {/* Language switch — always visible in navigation */}
                    {languagesEnabled && renderLanguageSwitch(true)}

                    {/* Desktop Navigation */}
                    <nav className="header-nav desktop-nav">
                        <button
                            className="nav-button"
                            onClick={handleHomeClick}
                            aria-label={t('nav.home')}
                        >
                            <Home size={18} />
                            <span className="nav-label">{t('nav.home')}</span>
                        </button>

                        <button
                            className="nav-button"
                            onClick={onSearchClick}
                            aria-label={t('title.search')}
                        >
                            <Search size={18} />
                            <span className="nav-label">{t('nav.search')}</span>
                        </button>

                        <button
                            className="nav-button"
                            onClick={handleCDRiskClick}
                            aria-label={t('nav.visitCdrisk')}
                        >
                            <Globe size={18} />
                            <span className="nav-label">CDRisk.com</span>
                        </button>

                        <button
                            className="nav-button nav-button-primary"
                            onClick={onLoginClick}
                            aria-label={t('nav.login')}
                        >
                            <User size={18} />
                            <span className="nav-label">{loginLabel || t('nav.login')}</span>
                        </button>
                    </nav>

                    {/* Mobile Menu Toggle */}
                    <button
                        className="mobile-menu-toggle"
                        onClick={toggleMobileMenu}
                        aria-label={t('nav.toggleSidebar')}
                        aria-expanded={mobileMenuOpen}
                    >
                        {mobileMenuOpen ? <X size={20} /> : <MoreVertical size={20} />}
                    </button>
                </div>
            </div>

            {/* Mobile Navigation Menu */}
            {mobileMenuOpen && (
                <nav className="header-nav mobile-nav">
                    {languagesEnabled && renderLanguageSwitch(false)}

                    <button
                        className="nav-button mobile-nav-button"
                        onClick={() => {
                            handleHomeClick();
                            setMobileMenuOpen(false);
                        }}
                    >
                        <Home size={18} />
                        <span className="nav-label">{t('nav.home')}</span>
                    </button>

                    <button
                        className="nav-button mobile-nav-button"
                        onClick={() => {
                            onSearchClick();
                            setMobileMenuOpen(false);
                        }}
                    >
                        <Search size={18} />
                        <span className="nav-label">{t('nav.search')}</span>
                    </button>

                    <button
                        className="nav-button mobile-nav-button"
                        onClick={() => {
                            handleCDRiskClick();
                            setMobileMenuOpen(false);
                        }}
                    >
                        <Globe size={18} />
                        <span className="nav-label">{t('nav.visitCdrisk')}</span>
                    </button>

                    <button
                        className="nav-button nav-button-primary mobile-nav-button"
                        onClick={() => {
                            onLoginClick();
                            setMobileMenuOpen(false);
                        }}
                    >
                        <User size={18} />
                        <span className="nav-label">{loginLabel || t('nav.clientLogin')}</span>
                    </button>
                </nav>
            )}
        </header>
    );
};

export default Header;
